import { WrappedColumnHeader } from "components/common/dataGrid/WrappedColumnHeader";
import { Box, Typography } from "@mui/material";
import { GridActionsCellItem, GridColDef } from "@mui/x-data-grid-premium";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import UndoIcon from "@mui/icons-material/Undo";
import { useMemo } from "react";
import { HotkeyCompliantDataGrid } from "components/common/dataGrid/HotkeyCompliantDataGrid";
import { useTenantTranslation, useUnitsFormatter } from "hooks/formatters";
import { useLanguage } from "hooks/settings";
import { useNotifyStatus } from "contexts/status";
import { useFormatNumber } from "src/utils/formatNumber";
import {
  MaterialPhysics,
  MaterialPhysicsOverride,
  MaterialRead,
  ParameterConflict,
  SupportedLanguage,
} from "store/api/generatedApi";
import { sorted } from "helpers";
import { MarkerDot } from "./MarkerDot";
import { ConflictMarkerDot } from "./ConflictMarkerDot";
import {
  PHYSICS_FIELDS,
  PHYSICS_DECIMAL_PLACES,
  parsePhysicsProperty,
  editablePhysicsEqual,
} from "./materialPhysicsEdits";
import { ConflictReasons } from "./ConflictReasons";

type PhysicsRow = MaterialPhysicsOverride & { materialName: string };

export const MaterialPhysicsDefaultsTable = ({
  materials,
  source,
  overrides,
  scrapchef,
  conflicts,
  chefGroupNameById,
  limitLabelBySignature,
  disabled,
  onUpdate,
  onDiscard,
}: {
  materials: MaterialRead[];
  source: MaterialPhysics[];
  overrides: MaterialPhysicsOverride[];
  scrapchef: MaterialPhysics[];
  conflicts: ParameterConflict[];
  chefGroupNameById: Record<number, string>;
  limitLabelBySignature: Record<string, string>;
  disabled: boolean;
  onUpdate: (physics: MaterialPhysicsOverride) => void;
  onDiscard: (materialId: number) => void;
}) => {
  const { t } = useTenantTranslation();
  const units = useUnitsFormatter(true);
  const language = useLanguage();
  const formatNumber = useFormatNumber();
  const notify = useNotifyStatus();
  const rows = useMemo(
    () =>
      sorted(
        materials.filter((material) => material.visibility === "visible"),
        (material) => material.ordering
      ).flatMap((material) => {
        const physics =
          overrides.find((physics) => physics.material_id === material.id) ??
          source.find((physics) => physics.material_id === material.id);
        return physics ? [{ ...physics, materialName: material.name }] : [];
      }),
    [materials, source, overrides]
  );
  const columns: GridColDef<PhysicsRow>[] = [
    {
      field: "materialName",
      headerName: t("material"),
      flex: 1,
      minWidth: 140,
      display: "flex",
    },
    ...PHYSICS_FIELDS.map(
      ({ field, label, units: unitType }): GridColDef<PhysicsRow> => ({
        field,
        headerName: `${t(label)}${unitType === null ? " (%)" : units(unitType)}`,
        flex: 1,
        minWidth: field === "electrical_energy_consumption" ? 230 : 160,
        editable: !disabled,
        display: "flex",
        valueGetter: (_: unknown, row) =>
          formatNumber(PHYSICS_DECIMAL_PLACES)(row[field]),
        valueSetter: (text: string, row) => {
          if (text.trim() === "") return row;
          try {
            return {
              ...row,
              [field]: parsePhysicsProperty({
                text,
                language: language as SupportedLanguage,
                field,
              }),
            };
          } catch {
            notify({ text: t("invalidNumber"), type: "error" });
            return row;
          }
        },
        renderCell: ({ row }) => {
          const original = source.find(
            (material) => material.material_id === row.material_id
          );
          const imported = scrapchef.find(
            (material) => material.material_id === row.material_id
          );
          const warnings = conflicts.filter((conflict) =>
            conflict.material_physics.some(
              (reference) =>
                reference.material_id === row.material_id &&
                reference.property === field
            )
          );
          return (
            <Box
              sx={{
                position: "relative",
                display: "flex",
                justifyContent: "flex-end",
                alignItems: "center",
                width: "100%",
                height: "100%",
                px: 1,
                bgcolor:
                  original?.[field] !== row[field]
                    ? "warning.light"
                    : undefined,
              }}
            >
              <Typography variant="body1Mono">
                {formatNumber(PHYSICS_DECIMAL_PLACES)(row[field])}
              </Typography>
              {imported && imported[field] !== row[field] ? (
                <MarkerDot
                  corner="bottom-left"
                  color="secondary.main"
                  label={t("differsFromDefault")}
                  tooltip={
                    <>
                      {t("scrapChefDefaults")}:{" "}
                      {formatNumber(PHYSICS_DECIMAL_PLACES)(imported[field])}
                      {unitType === null ? "%" : units(unitType)}
                    </>
                  }
                />
              ) : null}
              {warnings.length > 0 ? (
                <ConflictMarkerDot
                  conflicts={warnings}
                  corner="top-left"
                  tooltip={
                    <ConflictReasons
                      conflicts={warnings}
                      chefGroupNameById={chefGroupNameById}
                      limitLabelBySignature={limitLabelBySignature}
                    />
                  }
                />
              ) : null}
            </Box>
          );
        },
      })
    ),
    {
      field: "discard",
      type: "actions",
      headerName: t("discardUnsavedChanges"),
      renderHeader: () => (
        <WrappedColumnHeader label={t("discardUnsavedChanges")} />
      ),
      width: 220,
      getActions: ({ row }) => {
        if (
          disabled ||
          !overrides.some(
            (material) => material.material_id === row.material_id
          )
        )
          return [];
        return [
          <GridActionsCellItem
            key="discard"
            icon={<UndoIcon />}
            label={t("discardUnsavedChanges")}
            onClick={() => {
              onDiscard(row.material_id);
            }}
          />,
        ];
      },
    },
    {
      field: "restore",
      type: "actions",
      headerName: t("restoreScrapchefDefaults"),
      renderHeader: () => (
        <WrappedColumnHeader label={t("restoreScrapchefDefaults")} />
      ),
      width: 220,
      getActions: ({ row }) => {
        const imported = scrapchef.find(
          (material) => material.material_id === row.material_id
        );
        if (
          disabled ||
          imported === undefined ||
          editablePhysicsEqual(imported, row)
        )
          return [];
        return [
          <GridActionsCellItem
            key="scrapchef"
            icon={<RestartAltIcon />}
            label={t("restoreScrapchefDefaults")}
            onClick={() => {
              onUpdate(imported);
            }}
          />,
        ];
      },
    },
  ];
  return (
    <HotkeyCompliantDataGrid
      rows={rows}
      columns={columns}
      getRowId={(row) => row.material_id}
      processRowUpdate={(row) => {
        onUpdate(row);
        return row;
      }}
      disableRowSelectionOnClick
      sx={{ flex: 1, minHeight: 320 }}
    />
  );
};
